import { useMemo, useState } from 'react';
import { useRouter } from 'next/compat/router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMutation } from '@apollo/client';

import {
  ApproveInstoreCartDocument,
  UpdateKioskOrderPaymentDocument,
  UpdateOrderPaymentDocument,
} from 'src/gql/graphql';
import {
  useDutchiePayAnalytics,
  INSTORE_EVENTS,
} from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import { formatPaymentsError } from 'src/payments/hooks/use-payments-error/use-payments-error';
import { useAerosyncPreAuthContext } from 'src/payments/components/aerosync-pre-auth/hooks/use-aerosync-pre-auth';
import useErnie from 'shared/hooks/use-ernie';
import { ERNIE_TIMEOUTS, ERNIE_TYPES } from 'shared/constants';
import { getLogger } from 'src/utils/datadog-logger';
import { DUTCHIE_PAY, LOG_NAMESPACES } from 'src/payments/constants';
import { PBB_PAYMENT_SWITCH_FLAG, useLatchedFlag } from 'src/payments/dutchie-pay/flags';
import useUser from 'src/hooks/use-user';

const logger = getLogger(LOG_NAMESPACES.PBB_INSTORE_CART);

type UseCartApprovalProps = {
  cartData: { id: string } | undefined;
  dispensaryInfo: { name?: string; dispensaryId: string } | undefined;
  isKiosk?: boolean;
  onApproved?: () => void;
  orderId?: string;
  skipRouteChange?: boolean;
  stopPolling: () => void;
  tipAmount: number;
  updateOrderToken?: string;
  userId: string;
};

type UseCartApprovalReturn = {
  paymentLoading: boolean;
  paymentComplete: boolean;
  paymentUnsuccessful: boolean;
  onCartApprove: () => Promise<void>;
};

export const useCartApproval = ({
  cartData,
  dispensaryInfo,
  isKiosk: isKioskProp,
  onApproved,
  orderId: orderIdProp,
  skipRouteChange = false,
  stopPolling,
  tipAmount,
  updateOrderToken: updateOrderTokenProp,
  userId,
}: UseCartApprovalProps): UseCartApprovalReturn => {
  const flags = useFlags();
  const router = useRouter();
  const User = useUser();
  const { dutchiePayEventTracker } = useDutchiePayAnalytics();
  const { whiteLabelConfig, aggregator, paymentMethodId } = useDutchiePay();
  const showErnie = useErnie();
  const [approveInstoreCart] = useMutation(ApproveInstoreCartDocument);
  const [updateKioskOrderPayment] = useMutation(UpdateKioskOrderPaymentDocument);
  const [updateOrderPayment] = useMutation(UpdateOrderPaymentDocument);

  const { isKiosk: isKioskQuery, orderId: orderIdQuery, updateOrderToken: updateOrderTokenQuery } = (router?.query ??
    {}) as {
    isKiosk: string;
    orderId: string;
    updateOrderToken: string;
  };

  const orderId = orderIdProp ?? orderIdQuery;
  const updateOrderToken = updateOrderTokenProp ?? updateOrderTokenQuery;
  const isKiosk = isKioskProp ?? Boolean(isKioskQuery);

  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentUnsuccessful, setPaymentUnsuccessful] = useState(false);

  const dispensaryName = dispensaryInfo?.name ?? 'unknown';
  const dispensaryId = dispensaryInfo?.dispensaryId ?? '';
  const instoreCartId = cartData?.id ?? '';

  const eventTrackingProps = useMemo(
    () => ({
      dispensary: { name: dispensaryName, id: dispensaryId },
      instoreCartId,
    }),
    [dispensaryName, dispensaryId, instoreCartId]
  );

  // payment state management
  const setPaymentLoadingState = (): void => {
    setPaymentLoading(true);
  };

  const setPaymentCompleteState = (): void => {
    setPaymentLoading(false);
    setPaymentComplete(true);
  };

  const setPaymentFailedState = (): void => {
    setPaymentLoading(false);
    setPaymentUnsuccessful(true);
  };

  // Aerosync pre-auth check logic
  const enableAerosyncPreAuthCheck = flags['fintech.platform.enable-aerosync-balance-check.rollout'];
  const { triggerPreAuthFlow } = useAerosyncPreAuthContext();
  const runAerosyncPreAuthCheck = enableAerosyncPreAuthCheck && aggregator === 'aerosync' && paymentMethodId;
  const handlePreAuthCheck = async (): Promise<void> => {
    if (runAerosyncPreAuthCheck) {
      await triggerPreAuthFlow(paymentMethodId);
    }
  };

  // kiosk order validation
  const validateKioskConditions = (): boolean => {
    if (isKiosk && (!updateOrderToken || !orderId || !dispensaryInfo?.dispensaryId)) {
      setPaymentFailedState();
      dutchiePayEventTracker(INSTORE_EVENTS.CART_APPROVAL_FAILED, eventTrackingProps);
      logger.error('Kiosk order validation failed', {
        dispensaryId: dispensaryInfo?.dispensaryId ?? 'unknown',
        dispensaryName: dispensaryInfo?.name ?? 'unknown',
        userId,
        orderId,
        updateOrderToken,
      });
      return false;
    }
    return true;
  };

  // cart approval logic
  const handleCartApproval = async (): Promise<{ isAuthorized: boolean; authorization?: any }> => {
    const approveInstoreCartProps = {
      instoreCartId: cartData?.id ?? '',
      tipCents: tipAmount,
      sessionId: userId,
      skip: !cartData,
    };

    const approveResponse = await approveInstoreCart({
      variables: approveInstoreCartProps,
    });

    const authorization = approveResponse.data?.approveInstoreCart?.authorization;
    const isAuthorized = authorization?.authorized ?? false;

    if (!isAuthorized) {
      setPaymentFailedState();
      dutchiePayEventTracker(INSTORE_EVENTS.CART_APPROVAL_FAILED, eventTrackingProps);

      // Display enhanced error message from error_context when available
      const errorContext = authorization?.errorContext;
      if (errorContext?.consumerMessage) {
        showErnie(errorContext.consumerMessage, ERNIE_TYPES.DANGER, ERNIE_TIMEOUTS.LONG);
      }
    }

    return { isAuthorized, authorization };
  };

  // kiosk order payment update
  const handleKioskOrderPayment = async (): Promise<boolean> => {
    if (!isKiosk || !updateOrderToken || !orderId || !dispensaryInfo?.dispensaryId) {
      return false;
    }

    const kioskResponse = await updateKioskOrderPayment({
      variables: {
        input: {
          dispensaryId: dispensaryInfo.dispensaryId,
          id: orderId,
          updateOrderToken,
          paymentMethod: 'dutchiePay',
          paymentMethodId,
          tipCents: tipAmount,
        },
      },
    });

    return kioskResponse.data?.updateKioskOrderPayment?.success ?? false;
  };

  // ecomm PBB-switch path: defer the paymentMethod flip until after PAPI authorization, then
  // write the new method + final tip via updateOrderPayment. Flag-off falls through to the
  // legacy handlePaymentSuccess. Latched to survive LD identify-flickers between render and
  // click (see critical-patterns #1).
  const pbbPaymentSwitchEnabled = useLatchedFlag(PBB_PAYMENT_SWITCH_FLAG);
  const isEcommPbbSwitch =
    pbbPaymentSwitchEnabled &&
    !isKiosk &&
    Boolean(updateOrderToken) &&
    Boolean(orderId) &&
    Boolean(dispensaryInfo?.dispensaryId);
  const handleEcommOrderPayment = async (): Promise<boolean> => {
    if (!isEcommPbbSwitch || !dispensaryInfo) {
      return false;
    }

    const response = await updateOrderPayment({
      variables: {
        input: {
          dispensaryId: dispensaryInfo.dispensaryId,
          id: orderId,
          updateOrderToken,
          paymentMethod: DUTCHIE_PAY,
          paymentMethodId: paymentMethodId ?? null,
          tipCents: tipAmount,
        },
      },
    });

    return response.data?.updateOrderPayment?.success ?? false;
  };

  // error handling
  const handlePaymentError = (err: any): void => {
    console.error(err);
    setPaymentLoading(false);
    dutchiePayEventTracker(INSTORE_EVENTS.CART_APPROVAL_FAILED, eventTrackingProps);
    const { errorMarkdown, ernieType, ernieTimeout } = formatPaymentsError(err, whiteLabelConfig.brandedDutchiePayName);
    showErnie(errorMarkdown, ernieType, ernieTimeout);
    logger.error('Cart approval error', {
      error: err,
      dispensaryId: dispensaryInfo?.dispensaryId ?? 'unknown',
      dispensaryName: dispensaryInfo?.name ?? 'unknown',
      userId,
      orderId,
      updateOrderToken,
      isKiosk,
    });
    User.refetch();
  };

  // success handling
  const handlePaymentSuccess = (): void => {
    setPaymentCompleteState();
    dutchiePayEventTracker(INSTORE_EVENTS.CART_APPROVAL_SUCCEEDED, eventTrackingProps);
    onApproved?.();
  };

  // kiosk order handling
  const handleKioskOrderFlow = async (): Promise<void> => {
    const kioskSuccess = await handleKioskOrderPayment();

    if (kioskSuccess) {
      handlePaymentSuccess();
    } else {
      setPaymentFailedState();
      dutchiePayEventTracker(INSTORE_EVENTS.CART_APPROVAL_FAILED, eventTrackingProps);
    }
  };

  // ecomm PBB-switch order handling — runs the post-approval payment update so Mongo gets
  // paymentMethod=dutchiePay + the final tip persisted back to the order.
  const handleEcommOrderFlow = async (): Promise<void> => {
    const ecommSuccess = await handleEcommOrderPayment();

    if (ecommSuccess) {
      handlePaymentSuccess();
    } else {
      setPaymentFailedState();
      dutchiePayEventTracker(INSTORE_EVENTS.CART_APPROVAL_FAILED, eventTrackingProps);
      showErnie(
        "We couldn't finalize your Pay by Bank order. Please contact the dispensary to confirm your payment status.",
        ERNIE_TYPES.DANGER
      );
    }
  };

  const onCartApprove = async (): Promise<void> => {
    // Setup
    stopPolling();
    setPaymentLoadingState();
    if (!skipRouteChange) {
      void router?.replace(`/pay/instore`, undefined, { shallow: true });
    }
    dutchiePayEventTracker(INSTORE_EVENTS.CART_APPROVAL_CLICKED, eventTrackingProps);

    try {
      // Pre-auth check
      await handlePreAuthCheck();

      // Validate kiosk conditions
      if (!validateKioskConditions()) {
        return;
      }

      // Approve cart
      const { isAuthorized } = await handleCartApproval();
      if (!isAuthorized) {
        return;
      }

      // Handle kiosk vs ecomm-PBB-switch vs regular orders
      if (isKiosk) {
        await handleKioskOrderFlow();
      } else if (isEcommPbbSwitch) {
        await handleEcommOrderFlow();
      } else {
        handlePaymentSuccess();
      }
    } catch (err) {
      handlePaymentError(err);
    }
  };

  return {
    paymentLoading,
    paymentComplete,
    paymentUnsuccessful,
    onCartApprove,
  };
};
